import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Building2, PlusCircle, Home, Briefcase, Upload, FileText, X, Loader2, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
import { api, partnerHeaders, uploadFile, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
const SERVICES = ["Post-Construction Cleaning", "Move-In / Move-Out Cleaning", "General Deep Cleaning", "Property Detailing", "General Cleaning"];

function SubmitProperty({ onSubmitted }) {
  const [f, setF] = useState({ address: "", city: "", property_type: "", service_required: "", size_sqm: "", rooms: "", bathrooms: "", property_state: "", desired_date: "", priority_areas: "", notes: "" });
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return; setUploading(true);
    for (const file of files) { try { const res = await uploadFile(file); setMedia((p) => [...p, { ...res, preview: mediaUrl(res.url) }]); } catch { toast.error(`Upload failed: ${file.name}`); } }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/partners/properties", { ...f, media: media.map(({ path, url, original_filename, content_type }) => ({ path, url, original_filename, content_type })) }, { headers: partnerHeaders() });
      toast.success("Property submitted — we'll be in touch"); setF({ address: "", city: "", property_type: "", service_required: "", size_sqm: "", rooms: "", bathrooms: "", property_state: "", desired_date: "", priority_areas: "", notes: "" }); setMedia([]); onSubmitted();
    } catch { toast.error("Could not submit"); } finally { setBusy(false); }
  };
  return (
    <div className="ascendia-card p-6">
      <h3 className="font-display text-lg font-semibold text-white mb-4">Submit a property</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <input className={inputCls} placeholder="Address" value={f.address} onChange={upd("address")} />
        <input className={inputCls} placeholder="City" value={f.city} onChange={upd("city")} data-testid="partner-prop-city" />
        <select className={inputCls} value={f.service_required} onChange={upd("service_required")} data-testid="partner-prop-service"><option value="">Service required</option>{SERVICES.map((s) => <option key={s}>{s}</option>)}</select>
        <select className={inputCls} value={f.property_state} onChange={upd("property_state")}><option value="">Property state</option><option>Post-construction</option><option>Previously lived in</option></select>
        <input className={inputCls} placeholder="Size (sqm)" value={f.size_sqm} onChange={upd("size_sqm")} />
        <input className={inputCls} placeholder="Bathrooms" value={f.bathrooms} onChange={upd("bathrooms")} />
        <input type="date" className={inputCls} value={f.desired_date} onChange={upd("desired_date")} />
        <input className={inputCls} placeholder="Priority areas" value={f.priority_areas} onChange={upd("priority_areas")} />
      </div>
      <textarea className={`${inputCls} mt-4`} rows={2} placeholder="Notes" value={f.notes} onChange={upd("notes")} />
      <div className="mt-4">
        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[color:var(--border-strong)] bg-black/30 py-6 cursor-pointer hover:bg-white/[0.03]">
          <Upload className="h-5 w-5 text-gold-soft" /><span className="text-sm text-secondary">Upload photos or a walkthrough video</span>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={onUpload} data-testid="partner-prop-upload" />
        </label>
        {uploading && <p className="mt-2 text-sm text-muted-c flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>}
        {media.length > 0 && <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">{media.map((m) => (
          <div key={m.path} className="relative aspect-square rounded-lg overflow-hidden border border-[color:var(--border-subtle)] bg-black/40">
            {m.content_type?.startsWith("image") ? <img src={m.preview} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><FileText className="h-5 w-5 text-gold-soft" /></div>}
            <button onClick={() => setMedia((p) => p.filter((x) => x.path !== m.path))} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
          </div>))}</div>}
      </div>
      <button onClick={submit} disabled={busy} data-testid="partner-prop-submit" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50"><PlusCircle className="h-4 w-4" /> {busy ? "Submitting…" : "Submit property"}</button>
    </div>
  );
}

export default function PartnerDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("submit");
  const [props, setProps] = useState([]);
  const [jobs, setJobs] = useState([]);

  const load = () => {
    api.get("/partners/properties", { headers: partnerHeaders() }).then(({ data }) => setProps(data)).catch(() => {});
    api.get("/partners/jobs", { headers: partnerHeaders() }).then(({ data }) => setJobs(data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/partners/me").then(({ data }) => setMe(data)).catch(() => nav("/partners/login"));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot load on mount
  }, []);
  const logout = async () => {
    try { await api.post("/partners/logout"); } catch (e) { console.error("Partner logout request failed:", e); }
    nav("/partners/login");
  };

  return (
    <>
      <SEO title="Partner Dashboard" noindex />
      <div className="container-ascendia py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3"><Building2 className="h-6 w-6 text-gold-soft" /><div><h1 className="font-display text-2xl font-semibold text-white">{me?.company || "Partner"}</h1><p className="text-xs text-muted-c">{me?.contact_name} · {me?.business_type || "Partner"}</p></div></div>
          <button onClick={logout} data-testid="partner-logout" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="flex gap-2 mb-8 flex-wrap">
          {[["submit", "Submit property", PlusCircle], ["properties", `My properties (${props.length})`, Home], ["jobs", `Scheduled jobs (${jobs.length})`, Briefcase]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm border ${tab === k ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5"}`}><Icon className="h-4 w-4" /> {label}</button>
          ))}
        </div>

        {tab === "submit" && <SubmitProperty onSubmitted={() => { load(); setTab("properties"); }} />}

        {tab === "properties" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-properties-list">
            {props.map((p) => (
              <div key={p.id} className="ascendia-card p-5">
                <div className="flex justify-between items-start"><span className="font-mono text-xs text-gold-soft">{p.ref}</span><span className="text-[10px] px-2 py-0.5 rounded-full border border-white/20 text-white/70">{p.status}</span></div>
                <p className="mt-2 text-white font-medium">{p.service_required || "—"}</p>
                <p className="text-xs text-muted-c mt-1">{[p.city, p.size_sqm && `${p.size_sqm} sqm`, p.property_state].filter(Boolean).join(" · ")}</p>
                <p className="text-xs text-muted-c mt-1">{(p.media || []).length} media</p>
              </div>
            ))}
            {props.length === 0 && <p className="text-muted-c text-sm">No properties submitted yet.</p>}
          </div>
        )}

        {tab === "jobs" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-jobs-list">
            {jobs.map((j) => (
              <div key={j.id} className="ascendia-card p-5">
                <div className="flex justify-between items-start"><span className="font-mono text-xs text-gold-soft">{j.ref}</span><span className="text-[10px] px-2 py-0.5 rounded-full border border-white/20 text-white/70">{j.status}</span></div>
                <p className="mt-2 text-white font-medium">{j.service || "—"}</p>
                <p className="text-xs text-muted-c mt-1">{j.client_name} · {j.city} · {j.appointment_date || "No date"}</p>
                {j.report_ready ? (
                  <a href={`/portal/${j.portal_token}`} target="_blank" rel="noopener noreferrer" data-testid="partner-job-report-link" className="mt-3 inline-flex items-center gap-1 text-sm text-gold-soft hover:text-gold"><FileText className="h-4 w-4" /> View completed-job report <ExternalLink className="h-3 w-3" /></a>
                ) : <p className="mt-3 text-xs text-muted-c">Report not ready yet</p>}
              </div>
            ))}
            {jobs.length === 0 && <p className="text-muted-c text-sm">No scheduled jobs yet. Once we confirm a property you submitted, it will appear here.</p>}
          </div>
        )}
      </div>
    </>
  );
}
